import { z } from 'zod';

import { apiRequest, apiStream, ApiError } from '@/api/client';
import { themeSchema, type Theme } from '@/features/storefront/themeSchema';

/**
 * "Ask for changes" - the theme assistant's two routes.
 *
 * `POST /stores/me/themes/{id}/assist` answers in one JSON reply;
 * `POST …/assist/stream` answers the same request as Server-Sent Events:
 * `stage` lines while the AI works ("Rewriting the banner…"), then the same
 * result the JSON route returns. The component prefers the stream - a merchant
 * watching a spinner for eight seconds assumes it is broken - and falls back
 * to the JSON route when the stream cannot be opened at all.
 *
 * Nothing is saved by either route. The server edits the draft it was handed,
 * validates the result and hands the whole next document back; the editor
 * shows it as a preview and applies it only when the merchant keeps it.
 */

/** One change the assistant made, in the merchant's own language. */
export const themeAssistChangeSchema = z.object({
  op: z.string(),
  summary: z.string(),
});

/** One change it would not make, and why - in plain words, for the merchant. */
export const themeAssistRefusalSchema = z.object({
  op: z.string(),
  reason: z.string(),
});

/**
 * A picture the assistant thinks a section needs. It cannot make one itself -
 * the studio can - so the editor offers "Make this picture with AI" beside it.
 */
export const themeAssistImageSuggestionSchema = z.object({
  section_id: z.string(),
  block_id: z.string().nullish().transform((value) => value ?? null),
  prompt: z.string(),
});

/**
 * What "Ask for changes" answers with. Mirrors `ThemeAssistResponse` on
 * `app/schemas/store.py`.
 *
 * `config` is the whole next document - the draft that was sent, with the
 * change applied and validated - or `null` when nothing the AI proposed could
 * be done, in which case `message` says so. The three lists are new in the
 * second version of the route and default to empty, so an older server's
 * answer still parses.
 */
export const themeAssistResultSchema = z.object({
  summary: z.string(),
  operations_applied: z.number(),
  operations_refused: z.number().default(0),
  config: themeSchema.nullable(),
  message: z.string().nullable().optional(),
  changes: z.array(themeAssistChangeSchema).default([]),
  refused: z.array(themeAssistRefusalSchema).default([]),
  image_suggestions: z.array(themeAssistImageSuggestionSchema).default([]),
});
export type ThemeAssistResult = z.infer<typeof themeAssistResultSchema>;
export type ThemeAssistChange = z.infer<typeof themeAssistChangeSchema>;
export type ThemeAssistRefusal = z.infer<typeof themeAssistRefusalSchema>;
export type ThemeAssistImageSuggestion = z.infer<typeof themeAssistImageSuggestionSchema>;

/** One earlier turn of the conversation, so "make it bigger" knows what "it" is. */
export interface ThemeAssistTurn {
  role: 'merchant' | 'assistant';
  text: string;
}

/** How many earlier turns travel with a request. The server keeps no more. */
export const ASSIST_HISTORY_TURNS = 6;
/** The server's caps (`ThemeAssistRequest`): keys past this are dropped there anyway. */
export const ASSIST_CONTENT_KEYS = 2000;
const ASSIST_TURN_LENGTH = 500;

export interface ThemeAssistRequest {
  /** The merchant's words, English or Hindi. At most 500 characters. */
  message: string;
  /** The page id being edited - see `sections/pages.ts`. */
  page: string;
  /** The selected section, when the request is about one. */
  sectionId?: string | null;
  /** The editor's current draft, unsaved edits included. */
  config: Theme;
  /** Theme copy keys the AI may rewrite; any others are refused. */
  contentKeys?: string[];
  /** The conversation so far, oldest first. Only the last six turns are sent. */
  history?: readonly ThemeAssistTurn[];
}

function body(request: ThemeAssistRequest) {
  return {
    message: request.message,
    page: request.page,
    section_id: request.sectionId ?? null,
    config: request.config,
    content_keys: (request.contentKeys ?? []).slice(0, ASSIST_CONTENT_KEYS),
    // A long assistant summary would otherwise fail the whole request.
    history: (request.history ?? [])
      .slice(-ASSIST_HISTORY_TURNS)
      .map((turn) => ({ ...turn, text: turn.text.slice(0, ASSIST_TURN_LENGTH) })),
  };
}

/** One "Ask for changes" request against a draft. Counts as one AI draft. */
export function askForThemeChanges(
  themeId: string,
  request: ThemeAssistRequest,
  options: { signal?: AbortSignal } = {},
) {
  return askAt(`/stores/me/themes/${themeId}/assist`, request, options);
}

function askAt(path: string, request: ThemeAssistRequest, options: { signal?: AbortSignal }) {
  return apiRequest(path, themeAssistResultSchema, {
    method: 'POST',
    body: body(request),
    signal: options.signal,
  });
}

/**
 * The same request, streamed. `onStage` receives each progress line as it
 * arrives; the promise resolves with the result.
 *
 * The result may arrive as the shared stream's `done` event or as a `result`
 * event (the route's own name for it); both are read, so neither side has to
 * change for the other.
 */
export function streamThemeAssist(
  themeId: string,
  request: ThemeAssistRequest,
  options: AssistStreamOptions = {},
): Promise<ThemeAssistResult> {
  return streamAt(`/stores/me/themes/${themeId}/assist/stream`, request, options);
}

export interface AssistStreamOptions {
  signal?: AbortSignal;
  onStage?: (text: string) => void;
}

async function streamAt(
  path: string,
  request: ThemeAssistRequest,
  options: AssistStreamOptions,
): Promise<ThemeAssistResult> {
  let delivered: unknown;
  try {
    return await apiStream(path, themeAssistResultSchema, {
      method: 'POST',
      body: body(request),
      signal: options.signal,
      onEvent: (event, data) => {
        if (event === 'stage') {
          const text = (data as { text?: unknown } | null)?.text;
          if (typeof text === 'string' && text.trim()) options.onStage?.(text);
        } else if (event === 'result') {
          delivered = data;
        }
      },
    });
  } catch (error) {
    // A stream that sent `result` and then closed without `done` finished
    // its work; the closing is only the transport being tidy.
    if (delivered !== undefined && error instanceof ApiError && error.code === 'STREAM_INTERRUPTED') {
      const parsed = themeAssistResultSchema.safeParse(delivered);
      if (parsed.success) return parsed.data;
    }
    throw error;
  }
}

/**
 * Whether a failed stream is worth retrying as one JSON request.
 *
 * Yes when the stream itself never got going - no route, a proxy that cut it,
 * a network hiccup. No when the server answered with a reason: a used-up
 * allowance, a draft it refused, an AI that failed. Asking again through the
 * other door would only spend a second draft on the same refusal.
 */
export function shouldFallBackFromStream(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return false;
  if (!(error instanceof ApiError)) return true;
  if (error.code === 'STREAM_INTERRUPTED' || error.code === 'INVALID_RESPONSE') return true;
  return error.status === 404 || error.status === 405 || error.status === 0;
}

/**
 * Where "Ask for changes" sends its requests. The customiser's host supplies
 * one through `EditorServices.assist`: the merchant's library themes by
 * default, a catalog theme in the platform owner's customiser.
 */
export interface ThemeAssistTransport {
  stream: (themeId: string, request: ThemeAssistRequest, options?: AssistStreamOptions) => Promise<ThemeAssistResult>;
  ask: (themeId: string, request: ThemeAssistRequest, options?: { signal?: AbortSignal }) => Promise<ThemeAssistResult>;
}

export const STORE_THEME_ASSIST: ThemeAssistTransport = {
  stream: streamThemeAssist,
  ask: askForThemeChanges,
};

/**
 * A catalog theme, in the platform owner's customiser. The document id the
 * customiser passes is its own (`platform:<key>`), so the key is bound here.
 */
export function catalogThemeAssist(key: string): ThemeAssistTransport {
  const base = `/platform/themes/${encodeURIComponent(key)}/assist`;
  return {
    stream: (_themeId, request, options = {}) => streamAt(`${base}/stream`, request, options),
    ask: (_themeId, request, options = {}) => askAt(base, request, options),
  };
}
